import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"
import Sortable from "sortablejs"

// Connects to data-controller="color-preview-drop"
//
// Allows dragging report columns into the color preview to copy their colors
// Similar to formula-input drag & drop but copies colors instead of inserting text
//
// Usage:
//   .column-preview[
//     data-controller="color-preview-drop"
//   ]
//
export default class extends Controller {
  connect() {
    // Check if we've already wrapped this element
    if (this.element.parentNode?.classList?.contains('color-preview-drop-zone')) {
      this.wrapper = this.element.parentNode

      // Check if sortable already exists
      if (this.wrapper._sortable) {
        this.sortable = this.wrapper._sortable
        return
      }
    }

    // Create wrapper if we don't have one yet
    if (!this.wrapper) {
      // Wrap the preview in a drop zone
      const wrapper = document.createElement('div')
      wrapper.className = 'color-preview-drop-zone mb-3'
      wrapper.style.position = 'relative'
      wrapper.style.border = '2px dashed transparent' // Reserve space but invisible
      wrapper.style.borderRadius = '0.375rem'
      wrapper.style.transition = 'all 0.2s ease'

      // Insert wrapper before preview
      this.element.parentNode.insertBefore(wrapper, this.element)

      // Move preview into wrapper
      wrapper.appendChild(this.element)

      // Store reference to wrapper
      this.wrapper = wrapper
    }

    // Make wrapper accept drops from report-columns group
    this.sortable = Sortable.create(this.wrapper, {
      group: {
        name: 'color-preview-drop',
        pull: false,                    // Don't allow dragging out
        put: ['report-columns']         // Accept drops from report-columns group
      },

      sort: false,                      // Don't allow reordering
      animation: 0,                     // No animation

      // Hide the ghost element completely
      ghostClass: 'color-preview-drop-ghost',

      // Allow dropping anywhere in the wrapper
      draggable: '.column-item',        // Only accept column items

      onAdd: (evt) => {
        // Clear the hover styles
        this.wrapper.style.borderColor = 'transparent'
        this.wrapper.style.backgroundColor = ''

        this.handleColumnDrop(evt)
      },

      onMove: (evt) => {
        // Show border when hovering with dragged element
        this.wrapper.style.borderColor = '#0d6efd'
        this.wrapper.style.backgroundColor = 'rgba(13, 110, 253, 0.05)'
        return true // Allow the move
      }
    })

    // Add global drag end listener to remove hover styles
    document.addEventListener('dragend', () => {
      if (this.wrapper) {
        this.wrapper.style.borderColor = 'transparent'
        this.wrapper.style.backgroundColor = ''
      }
    })

    // Also listen to Sortable's onEnd
    const originalOnEnd = this.sortable.options.onEnd
    this.sortable.option('onEnd', (evt) => {
      this.wrapper.style.borderColor = 'transparent'
      this.wrapper.style.backgroundColor = ''
      if (originalOnEnd) {
        originalOnEnd(evt)
      }
    })

    // Use native drag events for better hover detection
    this.wrapper.addEventListener('dragenter', (e) => {
      e.preventDefault()
      this.wrapper.style.borderColor = '#0d6efd'
      this.wrapper.style.backgroundColor = 'rgba(13, 110, 253, 0.05)'
    })

    this.wrapper.addEventListener('dragover', (e) => {
      e.preventDefault() // Necessary to allow dropping
      // Keep the styles visible
      this.wrapper.style.borderColor = '#0d6efd'
      this.wrapper.style.backgroundColor = 'rgba(13, 110, 253, 0.05)'
    })

    this.wrapper.addEventListener('dragleave', (e) => {
      // Only clear if we're actually leaving the wrapper (not entering a child)
      if (e.target === this.wrapper) {
        this.wrapper.style.borderColor = 'transparent'
        this.wrapper.style.backgroundColor = ''
      }
    })

    this.wrapper.addEventListener('drop', (e) => {
      this.wrapper.style.borderColor = 'transparent'
      this.wrapper.style.backgroundColor = ''
    })

    // Store the sortable instance on the wrapper to prevent re-initialization
    this.wrapper._sortable = this.sortable
  }

  disconnect() {
    // Clean up sortable instance
    if (this.sortable) {
      this.sortable.destroy()
      if (this.wrapper) {
        delete this.wrapper._sortable
      }
    }
  }

  handleColumnDrop(evt) {
    const item = evt.item
    const fromList = evt.from
    const oldIndex = evt.oldIndex

    // Check if item came from the selected columns list (not from available columns)
    const fromSelectedColumnsList = fromList?.id === 'selectedColumnsList' ||
                                     fromList?.id === 'reportColumnsList' ||
                                     fromList?.closest('.column-list')?.id === 'reportColumnsList'

    // REJECT drops from available columns lists
    if (!fromSelectedColumnsList) {
      // Check if this was a cloneable item
      const isCloneable = item.classList.contains('cloneable')

      if (isCloneable) {
        item.remove()
      } else {
        if (oldIndex < fromList.children.length) {
          fromList.insertBefore(item, fromList.children[oldIndex])
        } else {
          fromList.appendChild(item)
        }
      }
      return
    }

    // Extract colors from dropped item
    const headerBgInput = item.querySelector('input[data-column-data="header_background_color"]')
    const headerTextInput = item.querySelector('input[data-column-data="header_text_color"]')
    const columnBgInput = item.querySelector('input[data-column-data="column_background_color"]')
    const columnTextInput = item.querySelector('input[data-column-data="column_text_color"]')

    if (headerBgInput && headerTextInput && columnBgInput && columnTextInput) {
      const colors = {
        headerBg: headerBgInput.value,
        headerText: headerTextInput.value,
        columnBg: columnBgInput.value,
        columnText: columnTextInput.value
      }

      logger.log('[Color Preview Drop] Copying colors from column:', colors)
      this.applyColors(colors)
    } else {
      logger.warn('[Color Preview Drop] ✗ No color data found in dropped column!')
    }

    // CRITICAL: Revert the drag operation (move item back)
    // Insert back at original position
    if (oldIndex < fromList.children.length) {
      fromList.insertBefore(item, fromList.children[oldIndex])
    } else {
      fromList.appendChild(item)
    }
  }

  applyColors(colors) {
    // Find all color inputs in the properties panel
    const headerBgInput = this.element.closest('.properties-content')?.querySelector('input[data-column-property="header_background_color"]')
    const headerBgTextInput = this.element.closest('.properties-content')?.querySelector('input[data-column-property="header_background_color_text"]')
    const headerTextInput = this.element.closest('.properties-content')?.querySelector('input[data-column-property="header_text_color"]')
    const headerTextTextInput = this.element.closest('.properties-content')?.querySelector('input[data-column-property="header_text_color_text"]')
    const columnBgInput = this.element.closest('.properties-content')?.querySelector('input[data-column-property="column_background_color"]')
    const columnBgTextInput = this.element.closest('.properties-content')?.querySelector('input[data-column-property="column_background_color_text"]')
    const columnTextInput = this.element.closest('.properties-content')?.querySelector('input[data-column-property="column_text_color"]')
    const columnTextTextInput = this.element.closest('.properties-content')?.querySelector('input[data-column-property="column_text_color_text"]')

    // Update all inputs and trigger their change events
    if (headerBgInput) {
      headerBgInput.value = colors.headerBg
      headerBgInput.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (headerBgTextInput) {
      headerBgTextInput.value = colors.headerBg
      headerBgTextInput.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (headerTextInput) {
      headerTextInput.value = colors.headerText
      headerTextInput.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (headerTextTextInput) {
      headerTextTextInput.value = colors.headerText
      headerTextTextInput.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (columnBgInput) {
      columnBgInput.value = colors.columnBg
      columnBgInput.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (columnBgTextInput) {
      columnBgTextInput.value = colors.columnBg
      columnBgTextInput.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (columnTextInput) {
      columnTextInput.value = colors.columnText
      columnTextInput.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (columnTextTextInput) {
      columnTextTextInput.value = colors.columnText
      columnTextTextInput.dispatchEvent(new Event('input', { bubbles: true }))
    }

    // CRITICAL: After all color inputs are updated and their updateProperty handlers run,
    // trigger a single auto-save event to persist all changes
    // Use setTimeout to ensure all input events have completed their processing
    setTimeout(() => {
      const autoSaveEvent = new CustomEvent('report-autosave:schedule', {
        bubbles: true,
        detail: { source: 'color-preview-drop', action: 'apply-colors' }
      })
      this.element.dispatchEvent(autoSaveEvent)
      logger.log('[Color Preview Drop] ✓ Colors applied successfully and auto-save triggered')
    }, 0)
  }
}
